import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import ResourceAPI from './ResourceAPI';
import Token from 'types/models/Token';

export interface MFASetupResponse {
  secretCode: string;
  session: string;
  message: string;
}

export interface MFAStatusResponse {
  mfaEnabled: boolean;
  mfaPreference: string | null;
  mfaSetupCompleted: boolean;
}

export interface MFAVerifyRequest {
  userCode: string;
  friendlyDeviceName?: string;
}

export interface MFAVerifyResponse {
  message: string;
  mfaEnabled: boolean;
}

export interface MFAAdminSetRequest {
  enabled: boolean;
}

export interface MFAAdminSetResponse {
  message: string;
  userId: number;
  mfaEnabled: boolean;
}

class MfaApiService {
  private baseUrl: string;

  private adminBaseUrl: string;

  private axiosInstance: AxiosInstance;

  constructor() {
    this.baseUrl = `${process.env.REACT_APP_SERVER_URL}/mfa`;
    this.adminBaseUrl = `${process.env.REACT_APP_SERVER_URL}/admin/users`;
    
    // Create a fresh axios instance without any interceptors
    // This prevents ResourceAPI interceptors from replacing our access token with id token
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_SERVER_URL,
    });
  }

  // Get access token from localStorage for MFA operations
  private static getAccessToken(): string | null {
    const localStorageToken = localStorage.getItem('cognito-token');
    console.log('MfaApiService - Checking for access token');
    
    if (!localStorageToken) {
      console.error('MfaApiService - No cognito-token found in localStorage');
      return null;
    }
    
    try {
      const cognitoToken: Token = JSON.parse(localStorageToken);
      console.log('MfaApiService - Token keys:', Object.keys(cognitoToken));
      
      if (!cognitoToken.access_token) {
        console.error('MfaApiService - No access_token in cognito token object');
        console.log('MfaApiService - Available keys:', Object.keys(cognitoToken));
      } else {
        console.log('MfaApiService - Access token found, length:', cognitoToken.access_token.length);
      }
      
      return cognitoToken.access_token || null;
    } catch (error) {
      console.error('MfaApiService - Error parsing cognito-token:', error);
      return null;
    }
  }

  // Get axios config with BOTH tokens
  // - Authorization header: id_token (for API Gateway Cognito Authorizer)
  // - X-Access-Token header: access_token (for Cognito MFA operations)
  private static getAxiosConfig(): AxiosRequestConfig {
    const localStorageToken = localStorage.getItem('cognito-token');
    
    if (!localStorageToken) {
      console.error('MfaApiService - No cognito-token in localStorage');
      throw new Error('No authentication token available. Please log in again.');
    }
    
    let idToken: string | null = null;
    let accessToken: string | null = null;
    
    try {
      const cognitoToken: Token = JSON.parse(localStorageToken);
      idToken = cognitoToken.id_token || null;
      accessToken = cognitoToken.access_token || null;
      
      console.log('MfaApiService - Token configuration:', {
        hasIdToken: !!idToken,
        hasAccessToken: !!accessToken,
        idTokenLength: idToken?.length,
        accessTokenLength: accessToken?.length
      });
    } catch (error) {
      console.error('MfaApiService - Error parsing token:', error);
      throw new Error('Failed to parse authentication token. Please log in again.');
    }
    
    if (!idToken) {
      throw new Error('No ID token available. Please log in again.');
    }
    
    if (!accessToken) {
      throw new Error('No access token available. Please log in again.');
    }
    
    console.log('MfaApiService - Sending id_token for API Gateway, access_token for MFA ops');
    
    return {
      headers: {
        Authorization: `Bearer ${idToken}`,  // For API Gateway Authorizer
        'X-Access-Token': accessToken,        // For Cognito MFA operations
        'Content-Type': 'application/json',
      },
    };
  }

  async setupMfa(): Promise<MFASetupResponse> {
    try {
      console.log('MfaApiService.setupMfa - Making request to:', `${this.baseUrl}/setup`);
      const config = MfaApiService.getAxiosConfig();
      console.log('MfaApiService.setupMfa - Using access token (not id token)');
      
      const response = await this.axiosInstance.post<MFASetupResponse>(
        '/mfa/setup',
        {},
        config
      );
      console.log('MfaApiService.setupMfa - Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('MfaApiService.setupMfa - Error:', error);
      console.error('MfaApiService.setupMfa - Error response:', error.response?.data);
      console.error('MfaApiService.setupMfa - Error status:', error.response?.status);
      throw error;
    }
  }

  async verifyMfa(userCode: string, friendlyDeviceName?: string): Promise<MFAVerifyResponse> {
    const response = await this.axiosInstance.post<MFAVerifyResponse>(
      '/mfa/verify',
      { userCode, friendlyDeviceName },
      MfaApiService.getAxiosConfig()
    );
    return response.data;
  }

  async disableMfa(): Promise<{ message: string; mfaEnabled: boolean }> {
    const response = await this.axiosInstance.delete<{ message: string; mfaEnabled: boolean }>(
      '/mfa/disable',
      MfaApiService.getAxiosConfig()
    );
    return response.data;
  }

  async getMfaStatus(): Promise<MFAStatusResponse> {
    const response = await this.axiosInstance.get<MFAStatusResponse>(
      '/mfa/status',
      MfaApiService.getAxiosConfig()
    );
    return response.data;
  }

  async adminSetUserMfa(userId: number, enabled: boolean): Promise<MFAAdminSetResponse> {
    // Admin operations use the standard ResourceAPI (id_token is fine for this)
    const response = await ResourceAPI.axiosInstance.post<MFAAdminSetResponse>(
      `${this.adminBaseUrl}/${userId}/mfa`,
      { enabled }
    );
    return response.data;
  }
}

const mfaApiService = new MfaApiService();
export default mfaApiService;

